import { Clear as ClearIcon, Search as SearchIcon } from '@mui/icons-material';
import {
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FilterContainer from '@/components/ui/FilterContainer';
import { LogLevel } from '@/lib/api';
import { isValidDate } from '@/lib/date';

interface LogFiltersProps {
  level: LogLevel | '';
  since: string;
  until: string;
  onFilterChange: (filters: { level?: LogLevel | ''; since?: string; until?: string }) => void;
  onSearch: () => void;
  onReset: () => void;
}

// Helper to parse display string (YYYY-MM-DD HH:mm:ss) to ISO string
const parseFromDisplay = (displayString: string): string => {
  if (!displayString) return '';
  // Assume input is JST, convert to UTC ISO string
  const date = new Date(displayString.replace(' ', 'T') + '+09:00'); // Append JST offset
  if (!isValidDate(date)) return ''; // Return empty if invalid
  return date.toISOString();
};

/**
 * Log filter component
 * Form for specifying search conditions such as log level and time period
 */
export const LogFilters: React.FC<LogFiltersProps> = ({
  level,
  since, // Expects YYYY-MM-DDTHH:mm format from parent (Logs.tsx) initially
  until, // Expects YYYY-MM-DDTHH:mm format from parent (Logs.tsx) initially
  onFilterChange,
  onSearch,
  onReset,
}) => {
  // Local state for display (YYYY-MM-DD HH:mm:ss)
  const [localLevel, setLocalLevel] = useState<LogLevel | ''>(level);
  // Initialize with formatted props, converting from YYYY-MM-DDTHH:mm to YYYY-MM-DD HH:mm:ss
  const [localSince, setLocalSince] = useState(since ? since.replace('T', ' ') : '');
  const [localUntil, setLocalUntil] = useState(until ? until.replace('T', ' ') : '');

  // Update local state if props change (e.g., after reset or initial load)
  useEffect(() => {
    setLocalLevel(level);
    setLocalSince(since ? since.replace('T', ' ') : '');
    setLocalUntil(until ? until.replace('T', ' ') : '');
  }, [level, since, until]);

  // Handle form submission
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Convert display format back to ISO for the hook
    const sinceISO = parseFromDisplay(localSince);
    const untilISO = parseFromDisplay(localUntil);

    onFilterChange({
      level: localLevel,
      since: sinceISO,
      until: untilISO,
    });
    onSearch(); // Trigger search with potentially updated ISO strings
  };

  // Reset filters
  const handleReset = () => {
    setLocalLevel('');
    setLocalSince('');
    setLocalUntil('');
    onReset(); // This will trigger the useEffect above via prop changes
  };

  const { t } = useTranslation(['logs', 'common']);

  return (
    <FilterContainer title={t('logs:filters.title')} onSubmit={handleSubmit}>
      <Grid container spacing={2} alignItems="center">
        <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '25%' } }}>
          <FormControl fullWidth>
            <InputLabel id="log-level-label">{t('logs:filters.logLevel')}</InputLabel>
            <Select
              labelId="log-level-label"
              id="log-level"
              value={localLevel}
              label={t('logs:filters.logLevel')}
              onChange={(e) => setLocalLevel(e.target.value as LogLevel | '')}
            >
              <MenuItem value="">{t('logs:filters.all')}</MenuItem>
              <MenuItem value={LogLevel.DEBUG}>DEBUG</MenuItem>
              <MenuItem value={LogLevel.INFO}>INFO</MenuItem>
              <MenuItem value={LogLevel.WARN}>WARN</MenuItem>
              <MenuItem value={LogLevel.ERROR}>ERROR</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '25%' } }}>
          <TextField
            id="since-date"
            label={t('logs:filters.startDate')}
            type="text" // Change type to text
            placeholder="YYYY-MM-DD HH:mm:ss"
            value={localSince}
            onChange={(e) => setLocalSince(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
            // Add validation feedback if needed
          />
        </Grid>
        <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '25%' } }}>
          <TextField
            id="until-date"
            label={t('logs:filters.endDate')}
            type="text" // Change type to text
            placeholder="YYYY-MM-DD HH:mm:ss"
            value={localUntil}
            onChange={(e) => setLocalUntil(e.target.value)}
            InputLabelProps={{
              shrink: true,
            }}
            fullWidth
            // Add validation feedback if needed
          />
        </Grid>
        <Grid component="div" sx={{ width: { xs: '100%', sm: '50%', md: '25%' } }}>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
            >
              {t('common:buttons.search')}
            </Button>
            <Button
              type="button"
              variant="outlined"
              startIcon={<ClearIcon />}
              onClick={handleReset}
            >
              {t('common:buttons.reset')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </FilterContainer>
  );
};

export default LogFilters;
